import React, { useEffect, useState } from 'react';

import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { AnimatePresence, motion } from 'framer-motion';
import platform from 'platform';

import komodoWalletDownloads from '@/data/KomodoWalletDownloads.json';
import { siteData } from '@/data/configs/site';
import gradientLinkArrowBlack from '@/public/images/lp/svgs/gradient-link-arrow-black.svg';
import gradientLinkArrow from '@/public/images/lp/svgs/gradient-link-arrow.svg';

function getDownloadURL(data: any, osFamily: string | undefined) {
  // Mobile
  if (osFamily == 'Android') return data.android;
  if (osFamily == 'iOS') return data.ios;
  // Desktop
  if (osFamily == 'Windows') return data.windows;
  if (osFamily == 'OS X') return data.macos;
  if (osFamily == 'Linux' || 'Ubuntu' || 'Debian') return data.linux;
  return data.default;
}

export function KomodoWalletBtnNav() {
  let router = useRouter();

  let isHomeLp = router.asPath === '/en/' || router.asPath === '/en';
  let isCommunityLp = router.asPath === '/en/community/';
  let isContactLp = router.asPath === '/en/contact/';
  let isDeveloperLp = router.asPath === '/en/developer/';
  const [isScrolledPastHero, setIsScrolledPastHero] = useState(!isHomeLp);
  const [downloadURL, setDownloadURL] = useState(siteData.downloadURL);
  // Debounce function
  const debounce = <F extends (...args: any[]) => any>(func: F, wait: number) => {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: Parameters<F>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  useEffect(() => {
    const handleScroll = () => {
      const firstSection = document.getElementById('hero-section');
      if (firstSection && isHomeLp) {
        const sectionBottom = firstSection.offsetTop + firstSection.offsetHeight;
        setIsScrolledPastHero(window.scrollY > sectionBottom);
      } else {
        setIsScrolledPastHero(true);
      }
    };
    handleScroll();
    window.addEventListener('scroll', debounce(handleScroll, 100));
    return () => window.removeEventListener('scroll', handleScroll);
  }, [router, isHomeLp]);
  useEffect(() => {
    //https://github.com/bestiejs/platform.js/blob/master/doc/README.md#platformosfamily
    let osFamily = platform?.os?.family;
    setDownloadURL(getDownloadURL(komodoWalletDownloads, osFamily));
  }, []);

  let linkHref = !isScrolledPastHero ? downloadURL : siteData.webDEXlink;
  if (isDeveloperLp) {
    linkHref = downloadURL;
  }

  return (
    <Link
      href={linkHref}
      className='desk-nav-launch_app relative flex h-[76px] w-[200px] items-center justify-center lg:ml-auto'
    >
      <motion.button
        type='button'
        className={`relative flex flex-row items-center rounded-[14px] border border-none px-3 py-2 font-sans text-base font-bold shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
          isScrolledPastHero ? 'justify-left dancing-bg pl-6' : 'justify-center bg-white'
        } ${isDeveloperLp ? '' : isScrolledPastHero ? 'text-white' : 'text-black'} h-[48px] w-[180px]`}
        style={{
          background: isCommunityLp
            ? 'linear-gradient(90deg, #493DB5 0%, #6DABD4 100%)'
            : isContactLp
              ? 'linear-gradient(90deg, #B8B0FF 0%, #FFF 100%)'
              : isDeveloperLp
                ? 'black'
                : isScrolledPastHero
                  ? 'radial-gradient(89.61% 109% at 50% 50%, rgba(109, 117, 192, 0.1) 0%, rgba(93, 114, 169, 0.77) 100%)'
                  : '',
        }}
        whileHover={
          isDeveloperLp
            ? {}
            : isScrolledPastHero
              ? { scale: 1.05 }
              : {
                  y: -4,
                  boxShadow: '0 4px 15px rgba(84, 104, 230, 0.6)',
                  transition: { duration: 0.2 },
                }
        }
        whileTap={!isDeveloperLp ? { scale: 0.95 } : {}}
      >
        {isDeveloperLp && (
          <div
            className='absolute -left-1/2 -top-1/2 z-[-11] h-[104%] w-[101%] translate-x-[49%] translate-y-[46%] rounded-[14px]'
            style={{ background: 'linear-gradient(86deg, #fff 9.38%, #9e75ff 58.86%)' }}
          ></div>
        )}

        <AnimatePresence mode='wait'>
          <motion.span
            key={isScrolledPastHero ? 'download' : 'launch'}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.2 }}
            // className={`left-1/2 -translate-x-1/2 transform`}
          >
            {!isScrolledPastHero || isDeveloperLp ? (
              <span className={`${isDeveloperLp ? 'developer-lp-nav-cta' : ''}`}>
                Download Wallet
              </span>
            ) : (
              <span className={`${isContactLp ? 'text-black' : 'text-white'}`}>Launch Wallet</span>
            )}
          </motion.span>
        </AnimatePresence>

        <AnimatePresence>
          {!isDeveloperLp && isScrolledPastHero && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
              className='absolute right-3'
            >
              <ExportedImage
                placeholder='empty'
                src={isContactLp ? gradientLinkArrowBlack : gradientLinkArrow}
                className='h-[19px] w-auto'
                width={16}
                height={17}
                alt='link'
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </Link>
  );
}
